/**
 * API client for user notifications.
 */

import { apiRequest } from "./client";

/* ---------- Types ---------- */

export type NotificationType =
  | "awaiting_input"
  | "execution_failed"
  | "task_completed"
  | "task_blocked"
  | "task_assigned"
  | "task_comment"
  | "info"
  | "capacity_deferred"
  | "memory_learned"
  | "memory_review";

export interface Notification {
  id: string;
  notification_type: NotificationType;
  title: string;
  description: string;
  source_type: string;
  source_id: string;
  agent_id: string | null;
  agent_name: string;
  read: boolean;
  dismissed: boolean;
  created_at: string;
  /** Project that owns the source entity — for cross-project navigation. */
  project_id?: string | null;
  /** Exact AwaitingInput row for answered-question cleanup. */
  awaiting_input_id?: string;
  project_name: string | null;
  project_color: string | null;
}

interface ApiResponse<T> {
  status: "success";
  data: T;
}

/* ---------- Endpoints ---------- */

export async function markAllRead(): Promise<NotificationSummary> {
  const res = await apiRequest<
    ApiResponse<{ marked: number; summary: NotificationSummary }>
  >("/api/v1/notifications/read-all/", { method: "POST", silent: true });
  return res.data.summary;
}

/** A coding-chat tool approval that blocks work, from any project. */
export interface PendingApproval {
  id: string;
  title: string;
  project_id: string;
  project_name: string;
  created_at: string;
  expires_at: string | null;
  task_id: string;
  conversation_id: string;
  /** The chat's agent, else "Claude Code" / "Codex" / "OpenCode". */
  agent_name?: string;
  /** What the approval card shows. `summary` may be empty. */
  task_title?: string;
  provider?: "claude" | "codex" | "opencode";
  tool?: string;
  summary?: string;
}

export type NotificationRowKind = NotificationType | "cli_error";

/** Where a row goes when opened; null targets make the row not clickable. */
export interface NotificationRowTarget {
  type: "task" | "frame" | "conversation" | "memory" | "release_notes";
  id: string | null;
  /** The chat or task title, for “Something went wrong in …”. */
  label: string | null;
}

/** One hub row: repeats collapsed, reading or clearing applies to every member. */
export interface NotificationRowData {
  id: string;
  source: "notification" | "error_log";
  member_ids: string[];
  kind: NotificationRowKind;
  error_kind: string;
  title: string;
  description: string;
  occurrences: number;
  latest_at: string;
  read: boolean;
  quiet: boolean;
  project_id: string | null;
  project_name: string | null;
  target: NotificationRowTarget | null;
}

/** The backend summary: the one count, Needs you and the latest collapsed rows. */
export interface NotificationSummary {
  count: number;
  approvals: PendingApproval[];
  rows: NotificationRowData[];
}

export const EMPTY_NOTIFICATION_SUMMARY: NotificationSummary = {
  count: 0,
  approvals: [],
  rows: [],
};

export async function getNotificationSummary(
  signal?: AbortSignal,
): Promise<NotificationSummary> {
  const res = await apiRequest<ApiResponse<NotificationSummary>>(
    "/api/v1/notifications/summary/",
    { silent: true, signal },
  );
  return res.data;
}

/** One highlight of the installed release the user has not seen yet. */
export interface ReleaseHighlight {
  /** The seen-tip ID that marks it seen. */
  id: string;
  text: string;
}

/** Persist published release notes for the running desktop build; resolves
 *  to the release's next unseen highlight, or null. */
export async function claimReleaseNotes(
  version: string,
  channel: "prod" | "preview",
  platform: "macos" | "windows",
): Promise<ReleaseHighlight | null> {
  const res = await apiRequest<
    ApiResponse<{ release_highlight?: ReleaseHighlight | null }>
  >("/api/v1/notifications/release-notes/", {
    method: "POST",
    body: { version, channel, platform },
    silent: true,
  });
  return res.data.release_highlight ?? null;
}

function rowMemberIds(rows: readonly NotificationRowData[]) {
  return {
    notification_ids: rows
      .filter((row) => row.source === "notification")
      .flatMap((row) => row.member_ids),
    error_log_ids: rows
      .filter((row) => row.source === "error_log")
      .flatMap((row) => row.member_ids),
  };
}

export async function markRowsRead(
  rows: readonly NotificationRowData[],
): Promise<NotificationSummary> {
  const res = await apiRequest<ApiResponse<NotificationSummary>>(
    "/api/v1/notifications/read/",
    { method: "POST", silent: true, body: rowMemberIds(rows) },
  );
  return res.data;
}

export async function clearRows(
  rows: readonly NotificationRowData[],
): Promise<NotificationSummary> {
  const res = await apiRequest<ApiResponse<NotificationSummary>>(
    "/api/v1/notifications/clear/",
    { method: "POST", silent: true, body: rowMemberIds(rows) },
  );
  return res.data;
}

export async function clearAllNotifications(): Promise<NotificationSummary> {
  const res = await apiRequest<
    ApiResponse<{ dismissed: number; summary: NotificationSummary }>
  >("/api/v1/notifications/dismiss-all/", { method: "POST", silent: true });
  return res.data.summary;
}

/** Review mode marks an individual server notification read outside the hub. */
export async function markRead(notificationId: string): Promise<void> {
  await apiRequest(`/api/v1/notifications/${notificationId}/read/`, {
    method: "POST",
    silent: true,
  });
}
